import type { LoadsArray } from "~/shared/schemas/loads-schema.ts";

import type { ShipmentDateRangeValue } from "../constants/shipment-date-filter.ts";
import { getShipmentDateRangeCutoff } from "../constants/shipment-date-filter.ts";

/**
 * Truncates a date to the start of its UTC calendar day. The Loads grid
 * renders pickup dates in UTC (see `formatDateToNumericUTC`), so the filter
 * must compare on the same UTC day boundary. Using a local-timezone boundary
 * (e.g. date-fns `startOfDay`) would disagree with the displayed date for
 * users west of UTC, hiding shipments that appear to be on the cutoff day.
 */
const startOfUtcDay = (date: Date): Date => {
	const utc = new Date(date);
	utc.setUTCHours(0, 0, 0, 0);
	return utc;
};

/**
 * Filters loads to those whose **pickup date** falls within the selected
 * timeframe. This deliberately evaluates the raw `pickupDate` field rather than
 * `loadDateValue` (which is coalesced from `pickupDate ?? loadDate`): the
 * control is driven by Pickup Date, and shipments without a pickup date must be
 * excluded even when they carry a `loadDate`. Rows with a missing or
 * unparseable `pickupDate` are dropped since they cannot be placed in a date
 * range.
 *
 * The filter presents a Pickup *Date* range, so both the cutoff and each
 * pickup are normalized to the start of their UTC calendar day before
 * comparing. This keeps the comparison date-based (not timestamp-based) and
 * aligned with the grid's UTC date rendering, so a pickup shown on the cutoff
 * day is never hidden regardless of the viewer's timezone.
 */
export const filterLoadsByPickupDate = (
	loads: LoadsArray,
	range: ShipmentDateRangeValue,
	now: Date = new Date(),
): LoadsArray => {
	const cutoff = startOfUtcDay(getShipmentDateRangeCutoff(range, now));
	return loads.filter((load) => {
		if (load.pickupDate == null || load.pickupDate === "") {
			return false;
		}
		const pickup = new Date(load.pickupDate);
		if (Number.isNaN(pickup.getTime())) {
			return false;
		}
		return startOfUtcDay(pickup) >= cutoff;
	});
};
